import React, { FC, useEffect, useRef, useState } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { navigate } from 'gatsby';
import { LinkTo } from '@agria/theme/src/components/linkTo/LinkTo';
import { ExpandSmall, Tick } from '@agria/theme/src/components/icon';
import type { HeaderChildItems } from '../headers/header/Header';

interface NavDropdownProps {
  childItem: HeaderChildItems;
}

const StyledNavDropdown = styled.div`
  ${({ theme: { colors, space, layers, borderRadii } }) => css`
    button {
      min-height: calc(${space.small} + 32px); // Padding + down chevron height

      svg {
        transition: rotate 0.2s ease;
      }

      &[aria-expanded='false'] svg {
        rotate: 180deg;
      }
    }

    ul {
      position: absolute;
      width: 288px;
      background-color: ${colors.utilityWhite};
      padding: ${space.medium};
      z-index: ${layers.headerSearch};
      border-radius: ${borderRadii.medium};
      box-shadow: 4px 16px 32px rgba(3, 36, 62, 0.16);
      transition: opacity 0.1s ease-in-out;

      &[inert] {
        opacity: 0;
      }

      a {
        display: flex;
        padding: ${space.xSmall};

        .navDropdownCurrentIconWrap {
          display: inline-flex;
          align-items: center;
          width: 0;
          height: 24px;
          opacity: 0;
          overflow: hidden;
          flex-shrink: 0;
          pointer-events: none;
          transition:
            width 0.15s ease-in-out,
            opacity 0.2s 0.1s ease-in-out;

          svg {
            flex-shrink: 0;
            max-width: unset;
          }
        }

        &.is-active .navDropdownCurrentIconWrap {
          width: 32px;
          opacity: 1;
        }
      }
    }
  `};
`;

export const NavDropdown: FC<NavDropdownProps> = ({ childItem }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const toggleChildNav = () => setIsOpen(!isOpen);

  const handleClickChildItem = (
    event: React.MouseEvent<HTMLAnchorElement>,
    url: string,
  ) => {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }

    const clickedLink = event.currentTarget;
    const linkList = clickedLink.closest('ul');

    linkList?.querySelectorAll('a.is-active').forEach((linkElement) => {
      linkElement.classList.remove('is-active');
    });

    clickedLink.classList.add('is-active');

    event.preventDefault();

    window.setTimeout(() => {
      setIsOpen(false);
      navigate(url);
    }, 350);
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node)
    ) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <StyledNavDropdown ref={dropdownRef}>
      <button type="button" onClick={toggleChildNav} aria-expanded={isOpen}>
        {childItem.link.label}
        <ExpandSmall aria-hidden="true" />
      </button>

      <ul inert={!isOpen ? 'true' : undefined} aria-hidden={!isOpen}>
        {childItem.navigationChildItems
          .filter((grandChildItem) => grandChildItem.link)
          .map((grandChildItem, grandChildIndex) => (
            <li key={grandChildIndex}>
              <LinkTo
                to={grandChildItem.link.url}
                onClick={(event: React.MouseEvent<HTMLAnchorElement>) =>
                  handleClickChildItem(event, grandChildItem.link.url)
                }
              >
                <span className="navDropdownCurrentIconWrap" aria-hidden="true">
                  <Tick size="small" />
                </span>
                {grandChildItem.link.label}
              </LinkTo>
            </li>
          ))}
      </ul>
    </StyledNavDropdown>
  );
};
